/**
 * MCP Servers API Service
 *
 * Provides API calls for managing organization-level MCP server configurations.
 */
import api from './api';

// Types
export interface MCPServer {
  id: string;
  name: string;
  display_name?: string;
  description?: string;
  url: string;
  supports_date_override: boolean;
  auth_type: string;
  auth_hint?: string;
  restricted_teams: string[];
  is_active: boolean;
  health_status?: string;
  last_health_check?: string;
  created_at: string;
  updated_at: string;
  last_used_at?: string;
  created_by_username?: string;
}

export interface MCPServerCreate {
  name: string;
  display_name?: string;
  description?: string;
  url: string;
  supports_date_override?: boolean;
  auth_type: string;
  auth_config?: Record<string, unknown>;
  restricted_teams?: string[];
}

export interface MCPServerUpdate {
  display_name?: string;
  description?: string;
  url?: string;
  supports_date_override?: boolean;
  auth_type?: string;
  auth_config?: Record<string, unknown>;
  restricted_teams?: string[];
  is_active?: boolean;
}

export interface MCPServerTestResult {
  success: boolean;
  message: string;
  latency_ms?: number;
}

export interface MCPServerAvailable {
  name: string;
  display_name?: string;
  description?: string;
}

export interface EncryptionStatus {
  configured: boolean;
  message: string;
}

// Tool types
export interface MCPTool {
  id: string;
  server_id: string;
  tool_name: string;
  display_name?: string;
  description?: string;
  input_schema: Record<string, unknown>;
  is_enabled: boolean;
  is_available: boolean;
  supports_date_override: boolean;
  date_override_detection_mode: 'auto' | 'manual';
  date_override_params: string[];
  last_refreshed_at?: string;
}

export interface MCPToolUpdate {
  is_enabled?: boolean;
  supports_date_override?: boolean;
  date_override_detection_mode?: 'auto' | 'manual';
}

export interface MCPToolRefreshResult {
  server_id: string;
  tools_added: number;
  tools_updated: number;
  tools_unavailable: number;
  total_tools: number;
  latency_ms: number;
}

// Valid auth types
export const AUTH_TYPES = [
  { value: 'none', label: 'None', description: 'No authentication required' },
  { value: 'bearer', label: 'Bearer Token', description: 'API key or Bearer token' },
  { value: 'basic', label: 'Basic Auth', description: 'Username and password' },
  { value: 'custom_headers', label: 'Custom Headers', description: 'Custom HTTP headers' },
  { value: 'oauth2_client_credentials', label: 'OAuth 2.0', description: 'OAuth 2.0 client credentials flow' },
] as const;

export type AuthType = typeof AUTH_TYPES[number]['value'];

// API Functions

/**
 * Check if encryption is configured for storing credentials.
 */
export async function getEncryptionStatus(): Promise<EncryptionStatus> {
  const response = await api.get<EncryptionStatus>('/settings/mcp-servers/encryption-status');
  return response.data;
}

/**
 * List all MCP servers for the current organization.
 */
export async function listMCPServers(includeInactive = false): Promise<MCPServer[]> {
  const response = await api.get<{ servers: MCPServer[] }>('/settings/mcp-servers', {
    params: { include_inactive: includeInactive },
  });
  return response.data.servers;
}

/**
 * Get a specific MCP server by ID.
 */
export async function getMCPServer(serverId: string): Promise<MCPServer> {
  const response = await api.get<MCPServer>(`/settings/mcp-servers/${serverId}`);
  return response.data;
}

/**
 * Create a new MCP server configuration.
 */
export async function createMCPServer(data: MCPServerCreate): Promise<MCPServer> {
  const response = await api.post<MCPServer>('/settings/mcp-servers', data);
  return response.data;
}

/**
 * Update an existing MCP server configuration.
 */
export async function updateMCPServer(serverId: string, data: MCPServerUpdate): Promise<MCPServer> {
  const response = await api.put<MCPServer>(`/settings/mcp-servers/${serverId}`, data);
  return response.data;
}

/**
 * Delete an MCP server configuration.
 */
export async function deleteMCPServer(serverId: string): Promise<void> {
  await api.delete(`/settings/mcp-servers/${serverId}`);
}

/**
 * Test connectivity to an MCP server.
 */
export async function testMCPServer(serverId: string): Promise<MCPServerTestResult> {
  const response = await api.post<MCPServerTestResult>(`/settings/mcp-servers/${serverId}/test`);
  return response.data;
}

/**
 * List available MCP servers for agent use (respects team restrictions).
 */
export async function listAvailableMCPServers(): Promise<MCPServerAvailable[]> {
  const response = await api.get<{ servers: MCPServerAvailable[] }>('/settings/mcp-servers/available');
  return response.data.servers;
}

// Tool API Functions

/**
 * List all tools for an MCP server.
 */
export async function listServerTools(
  serverId: string,
  includeDisabled = true,
  includeUnavailable = true
): Promise<MCPTool[]> {
  const response = await api.get<{ tools: MCPTool[] }>(
    `/settings/mcp-servers/${serverId}/tools`,
    {
      params: {
        include_disabled: includeDisabled,
        include_unavailable: includeUnavailable,
      },
    }
  );
  return response.data.tools;
}

/**
 * Refresh tools from MCP server.
 * Fetches tools from server, upserts to database, auto-detects date override support.
 */
export async function refreshServerTools(serverId: string): Promise<MCPToolRefreshResult> {
  const response = await api.post<MCPToolRefreshResult>(
    `/settings/mcp-servers/${serverId}/tools/refresh`
  );
  return response.data;
}

/**
 * Update a tool's settings (enable/disable, date override).
 */
export async function updateTool(
  serverId: string,
  toolId: string,
  data: MCPToolUpdate
): Promise<MCPTool> {
  const response = await api.patch<MCPTool>(
    `/settings/mcp-servers/${serverId}/tools/${toolId}`,
    data
  );
  return response.data;
}
